import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SubSink } from 'subsink';
import { ATTACHMENT_UPLOAD_STATUS } from '../../../../core/constants/attachments-upload-statue.enum';
import { SNACKBAR_ALERT_TYPES } from '../../../../core/constants/snackbar-alert-types.enum';
import { AttachmentFile } from '../../../../core/models/attachment-file.model';
import { ApiRequestService } from '../../../../core/services/api-request.service';
import { AttachmentsService } from '../../../../core/services/attachments.service';
import { SnackbarAlertService } from '../../../../core/services/snackbar-alert.service';

export enum REQUEST_TYPE {
    CREATE_REQUEST = 'createRequest',
    PROCESS_REQUEST = 'processRequest',
    RETURN_REQUEST = 'returnRequest',
    CREATE_TICKETING = 'createTicketing',
    CREATE_REPORT = 'createReport',
    CREATE_CALL_RECORD = 'createCallRecord',
    CREATE_USER_CLIENTS = 'createUserClients'
}

@Component({
    selector: 'app-upload-attachments',
    templateUrl: './upload-attachments.component.html',
    styleUrls: ['./upload-attachments.component.css']
})
export class UploadAttachmentsComponent implements OnInit, OnDestroy {
    @Input() requestId;
    @Input() reportId;
    @Input() uploadAttachmentRequestType: REQUEST_TYPE;
    @Input() userAttachment;
    attachmentList: AttachmentFile[];
    errorSnackbarAlert = SNACKBAR_ALERT_TYPES.ERROR;
    subs = new SubSink();

    constructor(
        private attachmentsService: AttachmentsService,
        private snackbarAlertService: SnackbarAlertService,
        private apiRequestService: ApiRequestService
    ) {}

    ngOnInit(): void {
        this.subscribeToAddedAttachments();
    }

    subscribeToAddedAttachments() {
        this.subs.sink = this.attachmentsService.onAddAttachment.subscribe((attachmentsList: AttachmentFile[]) => {
            //NOTE: attachmentsList will have error, valid, uploaded and uploading attachment
            const newlyAddedAttachments = attachmentsList.filter(i => i.uploadStatus === ATTACHMENT_UPLOAD_STATUS.NEW);
            if (this.uploadAttachmentRequestType === REQUEST_TYPE.PROCESS_REQUEST) {
                this.uploadFilesOnProcessingRequest(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.CREATE_REQUEST) {
                this.uploadFilesOnCreateRequest(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.RETURN_REQUEST) {
                this.uploadFilesOnReturnRequest(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.CREATE_TICKETING) {
                this.uploadFilesCreateTicketing(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.CREATE_REPORT) {
                this.uploadFilesCreateReport(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.CREATE_CALL_RECORD) {
                this.uploadFilesCreateCallRecord(newlyAddedAttachments);
            } else if (this.uploadAttachmentRequestType === REQUEST_TYPE.CREATE_USER_CLIENTS) {
                this.uploadFilesUserClients(newlyAddedAttachments);
            }
        });
    }

    uploadFilesOnProcessingRequest(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFile(this.requestId, attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }

    uploadFilesOnCreateRequest(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForRequester(attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    console.log(error);
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }

    uploadFilesOnReturnRequest(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForRequester(attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }
    uploadFilesCreateTicketing(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForTicketing(attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }
    uploadFilesCreateReport(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForReport(this.reportId, attachment, this.userAttachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }
    uploadFilesCreateCallRecord(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForCallRecord(attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }
    uploadFilesUserClients(newlyAddedAttachments) {
        newlyAddedAttachments.forEach(attachment => {
            attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADING;
            this.subs.sink = this.apiRequestService.uploadFilesForUserClients(attachment).subscribe(
                (response: any) => {
                    if (response) {
                        attachment['key'] = response.data.key;
                        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.UPLOADED;
                    }
                    this.attachmentsService.onAttachmentUpload.next(true);
                },
                error => {
                    this.onAttachmentUploadError(attachment);
                }
            );
        });
    }
    onAttachmentUploadError(attachment) {
        this.snackbarAlertService.openSnackBar('uploadFileFailureErrorMessage', this.errorSnackbarAlert);
        attachment.uploadStatus = ATTACHMENT_UPLOAD_STATUS.FAILED;
        attachment.hasError = true;
        attachment.errorMessage = 'uploadFileFailureErrorMessage';
        this.attachmentsService.onAttachmentUpload.next(true);
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
